import axios from 'axios';
import toast from 'react-hot-toast';

const BASE = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({ baseURL: BASE, timeout: 30000 });

// Attach token
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('ams_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Handle 401 — auto refresh
let refreshing = false;
api.interceptors.response.use(
  r => r,
  async err => {
    const orig = err.config;
    if (err.response?.status === 401 && !orig._retry && !orig.url?.includes('/auth/')) {
      if (!refreshing) {
        refreshing = true;
        try {
          const rt = localStorage.getItem('ams_refresh');
          const { data } = await axios.post(`${BASE}/auth/refresh`, { refresh_token: rt });
          localStorage.setItem('ams_token', data.data.accessToken);
          orig._retry = true;
          refreshing = false;
          return api(orig);
        } catch (refreshErr) {
          refreshing = false;
          console.error('[api] token refresh failed:', refreshErr?.message, 'path:', window.location.pathname);
          localStorage.removeItem('ams_token');
          localStorage.removeItem('ams_refresh');
          if (!window.location.pathname.startsWith('/superadmin')) {
            window.location.href = '/login';
          }
        }
      }
    }
    // Laravel's validation-failure response always wraps every field error
    // under the same generic message: "Validation failed" — the actual
    // reason (e.g. "The email must be a valid email address.") lives in a
    // separate `errors` object this previously never looked at, so every
    // validation failure showed the identical unhelpful toast no matter
    // which field or why. Surface the real per-field reason(s) instead.
    const fieldErrors = err.response?.data?.errors;
    const msg = fieldErrors
      ? Object.values(fieldErrors).flat().join(' ')
      : err.response?.data?.message || 'Request failed';
    // This is the ONE place a failed request's message is shown, so screens
    // don't toast the server message themselves (that showed it twice).
    // Downloads (responseType 'blob') are skipped: their error body is a
    // Blob, so the only text here would be "Request failed" while the
    // screen's own "Download failed: …" message is more useful.
    // id = the message: the same error firing twice (e.g. two identical
    // requests failing together) shows as one pop-up instead of a stack.
    // A tag conflict that offers share/move (data.conflict) is asked about by the screen instead.
    if (err.response?.status !== 401 && orig?.responseType !== 'blob' && !err.response?.data?.conflict) toast.error(msg, { id: `api-error:${msg}` });
    return Promise.reject(err);
  }
);

// ─── Helpers ──────────────────────────────────────────────
const get  = (url, params) => api.get(url, { params }).then(r => r.data);
const post = (url, data)   => api.post(url, data).then(r => r.data);
const put  = (url, data)   => api.put(url, data).then(r => r.data);
const patch= (url, data)   => api.patch(url, data).then(r => r.data);
const del  = (url)         => api.delete(url).then(r => r.data);

// ─── Auth ─────────────────────────────────────────────────
export const authApi = {
  login:          d => post('/auth/login', d),
  logout:         () => post('/auth/logout'),
  refresh:        d => post('/auth/refresh', d),
  me:             () => get('/auth/me'),
  changePassword: d => post('/auth/change-password', d),
};

// ─── Assets ───────────────────────────────────────────────
export const assetsApi = {
  list:    p => get('/assets', p),
  get:     id => get(`/assets/${id}`),
  create:  d => post('/assets', d),
  update:  (id, d) => put(`/assets/${id}`, d),
  // Units split from one FAR row, shown as one line (group_code)
  group:       code => get('/assets/group', { code }),
  updateGroup: d    => put('/assets/group', d),
  delete:  id => del(`/assets/${id}`),
  bulkAction: d => post('/assets/bulk-action', d),
  summary: () => get('/assets/summary'),
  testTools: () => get('/assets/test-tools'),
  hardDeleteAll: params => api.delete('/assets/all-hard', { params }).then(r => r.data),
  getColumnPrefs: () => get('/assets/column-prefs'),
  setColumnPrefs: columns => put('/assets/column-prefs', { columns }),
  getSubheader: () => get('/assets/subheader-config'),
  setSubheader: field => put('/assets/subheader-config', { field }),
  addAttachment: (id, fd) => api.post(`/assets/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  downloadAttachment: (assetId, attachId) => api.get(`/assets/${assetId}/attachments/${attachId}/download`, { responseType: 'blob' }),
};

// ─── FAR ──────────────────────────────────────────────────
export const farApi = {
  templates:       () => get('/far/templates'),
  templateColumns: id => get(`/far/templates/${id}/columns`),
  updateTemplateColumn: (id, colId, d) => api.put(`/far/templates/${id}/columns/${colId}`, d).then(r => r.data),
  deleteTemplateColumn: (id, colId)    => api.delete(`/far/templates/${id}/columns/${colId}`).then(r => r.data),
  updateTemplate: (id, d) => api.put(`/far/templates/${id}`, d).then(r => r.data),
  deleteTemplate: id      => api.delete(`/far/templates/${id}`).then(r => r.data),
  createTemplate:  d  => post('/far/templates', d),
  // 5 min: reading and checking a large file takes a while before the server
  // answers (big files then import in the background, see batchProgress).
  upload: (fd, onProgress) => api.post('/far/upload', fd, {
    timeout: 300000,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: e => onProgress && onProgress(Math.round(e.loaded*100/e.total)),
  }).then(r => r.data),
  batches: () => get('/far/batches'),
  batchProgress: id => get(`/far/batches/${id}/progress`),
  deleteBatchAssets: id => api.delete(`/far/batches/${id}/assets`).then(r => r.data),
  uploadRfid: (fd, onProgress) => api.post('/far/rfid-upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: e => onProgress && onProgress(Math.round(e.loaded*100/e.total)),
  }).then(r => r.data),
  downloadTemplate: (id, name) => api.get(`/far/templates/${id}/download`, { responseType: 'blob' }).then(res => {
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a'); a.href = url;
    a.download = `${(name||'FAR_Template').replace(/[^a-zA-Z0-9]/g,'_')}.xlsx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }),
};

// ─── Masters ──────────────────────────────────────────────
export const mastersApi = {
  // Divisions
  divisions:    p => get('/masters/divisions', p),
  createDiv:    d => post('/masters/divisions', d),
  updateDiv:    (id, d) => put(`/masters/divisions/${id}`, d),

  // Departments
  departments:  p => get('/masters/departments', p),
  createDept:   d => post('/masters/departments', d),
  updateDept:   (id, d) => put(`/masters/departments/${id}`, d),

  // Locations
  locations:    p => get('/masters/locations', p),
  locationTree: () => get('/masters/locations/tree'),
  createLoc:    d => post('/masters/locations', d),
  updateLoc:    (id, d) => put(`/masters/locations/${id}`, d),

  // Categories
  majorCategories:  p => get('/masters/major-categories', p),
  createMajorCat:   d => post('/masters/major-categories', d),
  updateMajorCat:   (id, d) => put(`/masters/major-categories/${id}`, d),
  minorCategories:  p => get('/masters/minor-categories', p),
  createMinorCat:   d => post('/masters/minor-categories', d),
  updateMinorCat:   (id, d) => put(`/masters/minor-categories/${id}`, d),

  // Vendors
  vendors:      p => get('/masters/vendors', p),
  createVendor: d => post('/masters/vendors', d),
  updateVendor: (id, d) => put(`/masters/vendors/${id}`, d),

  // Custodians
  custodians:       p => get('/masters/custodians', p),
  createCustodian:  d => post('/masters/custodians', d),
  updateCustodian:  (id, d) => put(`/masters/custodians/${id}`, d),

  // Zones & Units
  zones:        p => get('/masters/zones', p),
  createZone:   d => post('/masters/zones', d),
  updateZone:   (id, d) => put(`/masters/zones/${id}`, d),
  units:        p => get('/masters/units', p),
  createUnit:   d => post('/masters/units', d),
  updateUnit:   (id, d) => put(`/masters/units/${id}`, d),

  // Users
  users:        p => get('/masters/users', p),
  createUser:   d => post('/masters/users', d),
  updateUser:   (id, d) => put(`/masters/users/${id}`, d),
  resetPwd:     id => patch(`/masters/users/${id}/reset-password`),

  // Roles
  roles:        () => get('/masters/roles'),
  permissions:  () => get('/masters/permissions'),
  createRole:   d => post('/masters/roles', d),
  updateRole:   (id, d) => put(`/masters/roles/${id}`, d),
};

// ─── Transactions ─────────────────────────────────────────
export const txApi = {
  // Transfers
  transfers:      p => get('/transactions/transfers', p),
  createTransfer: d => post('/transactions/transfers', d),
  actionTransfer: (id, d) => patch(`/transactions/transfers/${id}/action`, d),

  // Disposals
  disposals:      p => get('/transactions/disposals', p),
  createDisposal: d => post('/transactions/disposals', d),

  // Maintenance
  maintenance:        p => get('/transactions/maintenance', p),
  createMaintenance:  d => post('/transactions/maintenance', d),
  upcomingMaintenance:p => get('/transactions/maintenance/upcoming', p),

  // Checkouts
  checkouts:    p => get('/transactions/checkouts', p),
  checkout:     d => post('/transactions/checkouts', d),
  checkin:      id => patch(`/transactions/checkouts/${id}/checkin`),

  // Tags
  mapTag:       d => post('/tags/map', d),
  unmapTag:     d => post('/tags/unmap', d),
  lookupTag:    v => get(`/tags/lookup/${encodeURIComponent(v)}`),
  printQueue:   d => post('/tags/print-queue', d),

  // Depreciation
  runDepreciation:  d => post('/depreciation/run', d),
  depSchedule:      () => get('/depreciation/schedule'),
};

// ─── Reports ──────────────────────────────────────────────
export const reportsApi = {
  dashboard:      p  => get('/reports/dashboard', p),
  assetRegister:  p  => get('/reports/asset-register', p),
  insurance:      p  => get('/reports/insurance', p),
  transfers:      p  => get('/reports/transfers', p),
  depreciation:   p  => get('/reports/depreciation', p),
  tagStatus:      () => get('/reports/tag-status'),
  ageAnalysis:    () => get('/reports/age-analysis'),
  maintenance:    p  => get('/reports/maintenance', p),
  exportExcel: (type, params = {}) => {
    const qs = new URLSearchParams({ report_type: type, ...params }).toString();
    return api.get(`/reports/export?${qs}`, { responseType: 'blob' }).then(res => {
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `AssetTrack_${type}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    });
  },
};

export default api;

// ─── Insurance ────────────────────────────────────────────
export const insuranceApi = {
  list:   ()      => api.get('/insurance').then(r=>r.data),
  create: d       => api.post('/insurance', d).then(r=>r.data),
  update: (id,d)  => api.put(`/insurance/${id}`, d).then(r=>r.data),
};

// ─── Notifications ────────────────────────────────────────
export const notificationsApi = {
  list:    (limit=20) => api.get('/notifications', { params:{limit} }).then(r=>r.data),
  markRead:(id)       => api.patch(`/notifications/${id}/read`).then(r=>r.data),
  markAll: ()         => api.patch('/notifications/all/read').then(r=>r.data),
};
